import React from "react";

const styles = {
  rightPanel: {
    width: "50%",
    padding: "15px",
    paddingLeft: "15px",
    overflowY: "auto",
  },
  emptyState: {
    padding: "40px 20px",
    color: "#999",
    fontSize: "12px",
  },
  errorBox: {
    padding: "8px 12px",
    marginTop: "12px",
    backgroundColor: "#ffebee",
    border: "1px solid #ffcdd2",
    color: "#c62828",
    fontSize: "12px",
  },
};

function RequestLogs({
  requestLogs,
  loading,
  error,
  onClearAll,
  onDismissLog,
  copyToClipboard,
}) {
  const getLogTitle = (log) => {
    const API_BASE_URL = log.url.split("/health")[0].split("/v1")[0];
    const urlPath = log.url.replace(API_BASE_URL, "");
    return `${log.method} ${urlPath}`;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const renderJsonWithCopyButtons = (obj, depth = 0) => {
    if (typeof obj !== "object" || obj === null) {
      return JSON.stringify(obj);
    }

    const indent = "  ".repeat(depth);
    const nextIndent = "  ".repeat(depth + 1);

    if (Array.isArray(obj)) {
      if (obj.length === 0) return "[]";
      const items = obj.map((item, i) => (
        <React.Fragment key={i}>
          {nextIndent}
          {renderJsonWithCopyButtons(item, depth + 1)}
          {i < obj.length - 1 ? "," : ""}
          <br />
        </React.Fragment>
      ));
      return (
        <>
          {"["}
          <br />
          {items}
          {indent}
          {"]"}
        </>
      );
    }

    const entries = Object.entries(obj);
    if (entries.length === 0) return "{}";

    const lines = entries.map(([key, value], i) => {
      const isIdField = key.endsWith("_id") || key === "id";
      const isLastEntry = i === entries.length - 1;

      if (isIdField && typeof value === "string") {
        return (
          <React.Fragment key={key}>
            {nextIndent}"{key}": "{value}"
            <button
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(value);
              }}
              style={{
                marginLeft: "8px",
                fontSize: "8px",
                cursor: "pointer",
                border: "1px solid white",
                background: "#f0f0f0",
                borderRadius: "2px",
              }}
              title={`Copy ${key}`}
            >
              [_]
            </button>
            {!isLastEntry && ","}
            <br />
          </React.Fragment>
        );
      }

      const valueRendered =
        typeof value === "object" && value !== null
          ? renderJsonWithCopyButtons(value, depth + 1)
          : JSON.stringify(value);

      return (
        <React.Fragment key={key}>
          {nextIndent}"{key}": {valueRendered}
          {!isLastEntry && ","}
          <br />
        </React.Fragment>
      );
    });

    return (
      <>
        {"{"}
        <br />
        {lines}
        {indent}
        {"}"}
      </>
    );
  };

  return (
    <div style={styles.rightPanel}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "12px",
          paddingBottom: "12px",
          borderBottom: "1px solid #e5e5e5",
        }}
      >
        <h2 style={{ fontSize: "14px", margin: 0 }}>
          Request Logs ({requestLogs.length})
        </h2>
        {requestLogs.length > 0 && (
          <button
            onClick={onClearAll}
            style={{
              padding: "4px 8px",
              fontSize: "11px",
              border: "1px solid #ddd",
              backgroundColor: "#fff",
              cursor: "pointer",
              borderRadius: "4px",
              color: "#666",
            }}
          >
            Clear All
          </button>
        )}
      </div>

      {loading && (
        <div
          style={{
            fontSize: "12px",
            color: "#666",
            padding: "12px",
            background: "#f5f5f5",
            borderRadius: "4px",
            marginBottom: "12px",
          }}
        >
          Loading...
        </div>
      )}

      {requestLogs.length === 0 ? (
        <div style={styles.emptyState}>
          No requests yet. Execute a request to see logs.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {requestLogs.map((log) => (
            <div
              key={log.id}
              style={{
                border: "1px solid #e5e5e5",
                borderRadius: "6px",
                overflow: "hidden",
                background:
                  log.status === "success"
                    ? "#f9fff9"
                    : log.status === "error"
                      ? "#fff9f9"
                      : "#f9f9f9",
              }}
            >
              <div
                style={{
                  padding: "8px 12px",
                  background:
                    log.status === "success"
                      ? "#e8f5e9"
                      : log.status === "error"
                        ? "#ffebee"
                        : "#f5f5f5",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #e5e5e5",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span
                    style={{
                      fontSize: "10px",
                      fontWeight: 600,
                      padding: "2px 6px",
                      borderRadius: "3px",
                      background:
                        log.status === "success"
                          ? "#4caf50"
                          : log.status === "error"
                            ? "#f44336"
                            : "#999",
                      color: "white",
                    }}
                  >
                    {log.method}
                  </span>
                  <span style={{ fontSize: "11px", color: "#666" }}>
                    {formatTimestamp(log.timestamp)}
                  </span>
                  {log.statusCode && (
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#666",
                        background: "#fff",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        border: "1px solid #ddd",
                      }}
                    >
                      {log.statusCode}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => onDismissLog(log.id)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#999",
                    cursor: "pointer",
                    fontSize: "16px",
                    padding: "0 4px",
                    lineHeight: 1,
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ padding: "12px" }}>
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    marginBottom: "8px",
                    fontFamily: "monospace",
                    color: "#333",
                  }}
                >
                  {getLogTitle(log)}
                </div>

                {(log.body || Object.keys(log.headers).length > 0) && (
                  <details style={{ marginBottom: "8px" }}>
                    <summary
                      style={{
                        fontSize: "10px",
                        color: "#666",
                        cursor: "pointer",
                        marginBottom: "4px",
                      }}
                    >
                      Request Details
                    </summary>
                    <div
                      style={{
                        fontSize: "10px",
                        fontFamily: "monospace",
                        background: "#f9f9f9",
                        padding: "6px",
                        borderRadius: "3px",
                        marginTop: "4px",
                      }}
                    >
                      {Object.keys(log.headers).length > 0 && (
                        <div style={{ marginBottom: "4px" }}>
                          <strong>Headers:</strong>
                          <pre
                            style={{ margin: "2px 0", whiteSpace: "pre-wrap" }}
                          >
                            {JSON.stringify(log.headers, null, 2)}
                          </pre>
                        </div>
                      )}
                      {log.body && (
                        <div>
                          <strong>Body:</strong>
                          <pre
                            style={{ margin: "2px 0", whiteSpace: "pre-wrap" }}
                          >
                            {JSON.stringify(log.body, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </details>
                )}

                {log.status === "success" && log.response && (
                  <div>
                    <div
                      style={{
                        fontSize: "10px",
                        color: "#666",
                        marginBottom: "4px",
                        fontWeight: 600,
                      }}
                    >
                      Response:
                    </div>
                    <pre
                      style={{
                        margin: 0,
                        fontSize: "11px",
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        background: "#fff",
                        padding: "8px",
                        borderRadius: "4px",
                        border: "1px solid #e5e5e5",
                        maxHeight: "300px",
                        overflowY: "auto",
                      }}
                    >
                      {renderJsonWithCopyButtons(log.response)}
                    </pre>
                  </div>
                )}

                {log.status === "error" && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#d32f2f",
                      background: "#ffebee",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #ffcdd2",
                    }}
                  >
                    <strong>Error:</strong> {log.error}
                  </div>
                )}

                {log.status === "pending" && (
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#666",
                      fontStyle: "italic",
                    }}
                  >
                    Waiting for response...
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {error && <div style={styles.errorBox}>{error}</div>}
    </div>
  );
}

export default RequestLogs;
